import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The two cards under the Do not call table: what the agent listens for, and the public
 * opt-out page.
 *
 * **The phrases are the language packs', and only theirs.** The card lists the phrases a native
 * speaker signed off, as one list (the design's), each tagged with its pack's language. A language whose pack has none is in
 * `languagesMissing` and is named as UNAVAILABLE — the design's rule for an incomplete pack
 * (§9.4). There is no English line standing in for it, here or anywhere.
 *
 * **The opt-out URL is shown in `--font-mono` with a copy button.** It is the thing a customer
 * types into a receipt template or reads out to a printer, character by character. The link
 * opens the public page on `apps/web-public`; nothing here submits to it.
 *
 * AMENDED 2026-09-25 (customer-portal review #68, #69). The phrases are grouped by language, with
 * the language named, the apostrophe-free duplicates the matcher needs folded away, each phrase
 * shown with a trailing "…" (the matcher catches anything that starts with it, which is also why
 * a pack holds word stems), and a few per language with "Show all". Human example sentences per
 * language would need a field the language packs do not carry yet. The opt-out link wraps inside
 * its box instead of being cut off; the copy button still copies the whole URL.
 */
import { useQuery } from "@tanstack/react-query";
import { Button, Icon, Mono } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import {
  PHRASE_MATCH_NOTE,
  PHRASE_SHOWN_COUNT,
  coveredText,
  deadlineText,
  missingLanguageText,
  readablePhraseOf,
} from "./do-not-call-copy.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { optOutPageQuery, refusalPhraseQuery } from "../../api/query.js";

export function RefusalPhraseCard(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const card = useQuery(refusalPhraseQuery(gateway));

  return (
    <div className="c-panel dnc-card">
      <p className="dnc-card-title">{portalText("finance", "m0986")}</p>
      {card.isError ? (
        <p className="c-note c-note-critical" role="alert">{portalText("finance", "m0987")}{card.error.message}
        </p>
      ) : card.data === undefined ? (
        <p className="c-note">{portalText("finance", "m0988")}</p>
      ) : (
        <>
          {/* Grouped by language (review #68); each phrase carries its pack's language so it
              is read (and spoken by a screen reader) in that language. */}
          <div className="dnc-phrase-groups" role="group" aria-label={coveredText(card.data.languagesCoveredCount)}>
            {card.data.languageSet.map((set) => (
              <PhraseGroup
                key={set.languageCode}
                languageCode={set.languageCode}
                languageName={set.languageName}
                phrase={readablePhraseOf(set.phrase)}
              />
            ))}
          </div>
          <p className="dnc-card-foot">{PHRASE_MATCH_NOTE()}</p>
          {card.data.languagesMissing.length === 0 ? null : (
            <p className="c-note c-note-critical">
              {missingLanguageText(card.data.languagesMissing)}
            </p>
          )}
          <p className="dnc-card-foot">{portalText("finance", "m0989")}{deadlineText(card.data.deadlineMs)}.
          </p>
        </>
      )}
    </div>
  );
}

/** One language's phrases: the language named, a few shown, the rest behind "Show all". */
function PhraseGroup({
  languageCode,
  languageName,
  phrase,
}: {
  readonly languageCode: string;
  readonly languageName: string;
  readonly phrase: readonly string[];
}): ReactNode {
  usePortalTranslation("finance");
  const [all, setAll] = useState(false);
  const shown = all ? phrase : phrase.slice(0, PHRASE_SHOWN_COUNT);
  return (
    <div className="dnc-phrase-group">
      <p className="dnc-phrase-language">{languageName}</p>
      <ul className="dnc-phrases">
        {shown.map((one) => (
          <li key={one} className="dnc-phrase" lang={languageCode}>
            <span className="dnc-phrase-glyph">
              <Icon glyph={GLYPH.quotes} size="md" />
            </span>
            {one}…
          </li>
        ))}
      </ul>
      {phrase.length <= PHRASE_SHOWN_COUNT ? null : (
        <button
          type="button"
          className="dnc-phrase-more"
          aria-expanded={all}
          onClick={() => {
            setAll((open) => !open);
          }}
        >
          {all ? portalText("finance", "m0990") : portalText("finance", "m0991", { value1: String(phrase.length) })}
        </button>
      )}
    </div>
  );
}

type CopyState = "idle" | "copied" | "refused";

/** `https://wetalk.io/stop/roma-pizzeria` → `wetalk.io/stop/roma-pizzeria`. */
export function urlWithoutScheme(url: string): string {
  return url.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
}

export function OptOutPageCard(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const page = useQuery(optOutPageQuery(gateway));
  const [copyState, setCopyState] = useState<CopyState>("idle");

  return (
    <div className="c-panel dnc-card">
      <p className="dnc-card-title">{portalText("finance", "m0992")}</p>
      <p className="dnc-card-text">{portalText("finance", "m0993")}</p>
      {page.isError ? (
        <p className="c-note c-note-critical" role="alert">{portalText("finance", "m0994")}{" "}
          {page.error.message}
        </p>
      ) : page.data === undefined ? (
        <p className="c-note">{portalText("finance", "m0995")}</p>
      ) : (
        <>
          <p className="dnc-url">
            {/* Printed as the design prints it — host and path, no scheme — because that is
                what goes on a receipt; "Open the page" and "Copy the link" carry the full URL. */}
            <Mono wrap>{urlWithoutScheme(page.data.publicUrl)}</Mono>
          </p>
          <div className="c-row dnc-card-actions">
            <a
              className="btn btn-secondary"
              href={page.data.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
            >{portalText("finance", "m0996")}</a>
            <Button
              variant="ghost"
              onClick={() => {
                const url = page.data.publicUrl;
                // `navigator.clipboard` is absent outside a secure context; reaching for it
                // inside the promise turns that into a refusal the person is told about.
                Promise.resolve()
                  .then(() => navigator.clipboard.writeText(url))
                  .then(
                    () => {
                      setCopyState("copied");
                    },
                    () => {
                      setCopyState("refused");
                    },
                  );
              }}
            >{portalText("finance", "m0997")}</Button>
          </div>
          {copyState === "copied" ? (
            <p className="c-note" role="status">{portalText("finance", "m0998")}</p>
          ) : null}
          {copyState === "refused" ? (
            <p className="c-note c-note-critical" role="alert">{portalText("finance", "m0999")}</p>
          ) : null}
        </>
      )}
    </div>
  );
}
